import { useMemo, useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { archetypes, archetypeOrder } from '../data/archetypes';
import { archetypeRelations } from '../data/synastry';
import StarField from '../components/StarField';
import PlanetCanvas from '../components/PlanetCanvas';
import DrawingCanvas from '../components/DrawingCanvas';
import Oracle from '../components/Oracle';
import PlanetLandscape from '../components/PlanetLandscape';
import RadarChart from '../components/RadarChart';
import VoucherPrint from '../components/VoucherPrint';
import {
  StarDust,
  AuroraRings,
  GridOverlay,
  CornerDecorations,
} from '../components/CosmicEffects';
import {
  generatePersonalizedFeature,
  generatePersonalizedAlienation,
  generatePersonalizedVacation,
} from '../utils/personalizedResult';
import {
  recordArchetype,
  getRarityPercentage,
  hasRecorded,
  markRecorded,
} from '../utils/archetypeStats';
import styles from './Result.module.css';

// 获取相性原因描述
function getCompatReason(primaryKey, targetKey, type, archetypes) {
  const primaryName = archetypes[primaryKey]?.name || primaryKey;
  const targetName = archetypes[targetKey]?.name || targetKey;
  
  if (type === '互补') {
    const complementReasons = {
      'ruler-caregiver': `${primaryName}善于掌控全局、制定规则，而${targetName}擅长关怀他人、营造温暖。你们一个负责方向，一个负责人心，是天生的搭档。`,
      'ruler-sage': `${primaryName}追求权威与秩序，${targetName}追求智慧与真理。你们的结合让决策既有力量又有深度。`,
      'hero-caregiver': `${primaryName}勇于挑战、保护弱者，${targetName}温柔体贴、治愈人心。刚柔并济，你们能给彼此最好的支持。`,
      'hero-innocent': `${primaryName}渴望证明自己的价值，${targetName}相信世界的美好。你们的关系充满了守护与被守护的温情。`,
      'sage-creator': `${primaryName}追求深度理解，${targetName}追求独特表达。思想与创意的碰撞，能产生最精彩的火花。`,
      'sage-explorer': `${primaryName}渴望知识，${targetName}渴望体验。你们一起探索，既有深度又有广度。`,
      'explorer-creator': `${primaryName}热爱冒险与自由，${targetName}热爱创造与表达。你们都不走寻常路，能一起开拓新天地。`,
      'outlaw-magician': `${primaryName}敢于打破规则，${targetName}善于转化现实。你们都是改变世界的人，只是方式不同。`,
      'outlaw-creator': `${primaryName}质疑一切，${targetName}创造一切。叛逆与创新的结合，能带来真正的变革。`,
      'magician-innocent': `${primaryName}相信愿景的力量，${targetName}相信纯真的力量。你们都是理想主义者，能一起把梦想变成现实。`,
      'magician-creator': `${primaryName}善于想象可能性，${targetName}善于实现可能性。从构想到落地，你们是完美组合。`,
      'lover-caregiver': `${primaryName}追求深度连接，${targetName}乐于付出关爱。你们的关系充满了爱与温暖。`,
      'lover-innocent': `${primaryName}渴望亲密与美，${targetName}保持纯真与信任。你们能创造童话般的美好关系。`,
      'jester-everyman': `${primaryName}带来欢笑与轻松，${targetName}带来真实与归属。你们在一起，生活变得简单快乐。`,
      'jester-explorer': `${primaryName}享受当下的乐趣，${targetName}追求新鲜的体验。你们都热爱生活，能一起发现无穷乐趣。`,
      'everyman-caregiver': `${primaryName}追求真实的连接，${targetName}给予无私的关怀。你们的关系踏实而温暖。`,
      'innocent-hero': `${primaryName}相信美好，${targetName}守护美好。你们的关系充满了信任与安全感。`,
      'innocent-magician': `${primaryName}保持纯真的信念，${targetName}创造神奇的可能。你们都相信奇迹，能一起见证奇迹。`,
    };
    
    const key = `${primaryKey}-${targetKey}`;
    const reverseKey = `${targetKey}-${primaryKey}`;
    return complementReasons[key] || complementReasons[reverseKey] || 
      `${primaryName}与${targetName}拥有互补的特质。你们各自的优势能弥补对方的盲点，在一起时能发挥出1+1>2的效果。`;
  }
  
  if (type === '镜像') {
    const mirrorReasons = {
      'ruler-hero': `${primaryName}与${targetName}都追求卓越与成就，都有强烈的责任感。你们像镜子一样映照彼此，能深刻理解对方的驱动力。`,
      'sage-magician': `${primaryName}与${targetName}都追求超越表象的真相。你们都是思想者，能在精神层面产生深度共鸣。`,
      'explorer-outlaw': `${primaryName}与${targetName}都渴望自由，都不愿被束缚。你们理解彼此对独立的需求，能给对方足够的空间。`,
      'creator-lover': `${primaryName}与${targetName}都追求美与独特性。你们都重视自我表达，能欣赏彼此的独特之处。`,
      'caregiver-innocent': `${primaryName}与${targetName}都相信善良的力量。你们都有一颗柔软的心，能创造充满爱的关系。`,
      'jester-innocent': `${primaryName}与${targetName}都保持着对生活的热情与乐观。你们都不愿被沉重压垮，能一起保持轻盈。`,
      'everyman-lover': `${primaryName}与${targetName}都重视连接与归属。你们都渴望被接纳，能给彼此真正的理解。`,
    };
    
    const key = `${primaryKey}-${targetKey}`;
    const reverseKey = `${targetKey}-${primaryKey}`;
    return mirrorReasons[key] || mirrorReasons[reverseKey] || 
      `${primaryName}与${targetName}是镜像原型，你们在对方身上能看到自己的影子。这种相似让你们天然理解彼此，无需太多解释就能产生共鸣。`;
  }
  
  return '';
}

export default function Result() {
  const { state, dispatch } = useApp();
  const [showDrawing, setShowDrawing] = useState(false);
  const [showOracle, setShowOracle] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [showLandscape, setShowLandscape] = useState(false);
  const [showVoucher, setShowVoucher] = useState(false);
  const [landscapeImageUrl, setLandscapeImageUrl] = useState(null);
  const [textVisible, setTextVisible] = useState({ feature: false, alienation: false, vacation: false });
  const [scrollY, setScrollY] = useState(0);
  const [rarityPercent, setRarityPercent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { primary, primaryKey, secondary, secondaryKey, all, personalizedTexts, compatiblePlanets } = useMemo(() => {
    const sorted = Object.entries(state.scores).sort((a, b) => b[1] - a[1]);
    const primaryKey = sorted[0] ? sorted[0][0] : 'jester';
    const secondaryKey = sorted[1] ? sorted[1][0] : null;
    const primary = archetypes[primaryKey];
    const secondary = sorted[1] ? archetypes[sorted[1][0]] : null;
    const all = archetypeOrder.map((key) => ({
      key,
      ...archetypes[key],
      score: state.scores[key] || 0,
    }));
    
    // 生成个性化文本
    const personalizedTexts = {
      feature: generatePersonalizedFeature(state.scores, primaryKey, state.answers),
      alienation: generatePersonalizedAlienation(state.scores, primaryKey),
      vacation: generatePersonalizedVacation(state.scores, primaryKey, state.answers),
    };
    
    // 计算相性星球
    const relations = archetypeRelations[primaryKey] || {};
    const compatiblePlanets = {
      complement: (relations.complement || []).slice(0, 2).map(key => ({
        key,
        ...archetypes[key],
        type: '互补',
        reason: getCompatReason(primaryKey, key, '互补', archetypes),
      })),
      mirror: (relations.mirror || []).slice(0, 1).map(key => ({
        key,
        ...archetypes[key],
        type: '镜像',
        reason: getCompatReason(primaryKey, key, '镜像', archetypes),
      })),
    };
    
    return { primary, primaryKey, secondary, secondaryKey, all, personalizedTexts, compatiblePlanets };
  }, [state.scores, state.answers]);

  // 文字淡入效果
  useEffect(() => {
    const timers = [
      setTimeout(() => setTextVisible(v => ({ ...v, feature: true })), 500),
      setTimeout(() => setTextVisible(v => ({ ...v, alienation: true })), 1200),
      setTimeout(() => setTextVisible(v => ({ ...v, vacation: true })), 1900),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // 记录原型统计
  useEffect(() => {
    if (primaryKey && !hasRecorded()) {
      recordArchetype(primaryKey);
      markRecorded();
    }
    setRarityPercent(getRarityPercentage(primaryKey));
  }, [primaryKey]);

  // 滚动视差效果
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setScrollY(container.scrollTop);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShare = () => dispatch({ type: 'SET_PAGE', payload: 'share' });
  const handleBack = () => dispatch({ type: 'SET_PAGE', payload: 'landing' });

  // 手机端跳转独立页面，桌面端显示弹窗
  const handleRadarClick = () => {
    if (isMobile) {
      dispatch({ type: 'SET_PAGE', payload: 'radarPage' });
    } else {
      setShowRadar(true);
    }
  };

  const handleOracleClick = () => {
    if (isMobile) {
      dispatch({ type: 'SET_PAGE', payload: 'oracle' });
    } else {
      setShowOracle(true);
    }
  };

  const handleDrawingClick = () => {
    if (isMobile) {
      dispatch({ type: 'SET_PAGE', payload: 'drawing' });
    } else {
      setShowDrawing(true);
    }
  };

  const parallaxOffset = scrollY * 0.3;

  return (
    <div className={styles.container} ref={containerRef}>
      <StarField density={80} speed={0.2} />
      {!isMobile && (
        <>
          <GridOverlay />
          <AuroraRings />
          <StarDust count={25} />
          <CornerDecorations />
        </>
      )}

      <button className={styles.backBtn} onClick={handleBack}>
        ← 返回首页
      </button>

      <div className={styles.layout}>
        <div
          className={styles.left}
          style={{ transform: `translateY(${-parallaxOffset * 0.5}px)` }}
        >
          {/* 左侧装饰元素 */}
          <div className={styles.leftDecorations}>
            {/* 轨道环 */}
            <div className={styles.orbitRing} data-ring="1" />
            <div className={styles.orbitRing} data-ring="2" />
            <div className={styles.orbitRing} data-ring="3" />
            
            {/* 星座连线装饰 */}
            <svg className={styles.constellationLines} viewBox="0 0 400 400">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(180, 160, 220, 0.6)" />
                  <stop offset="100%" stopColor="rgba(100, 80, 150, 0.2)" />
                </linearGradient>
              </defs>
              {/* 左上星座 */}
              <g className={styles.constellation} data-pos="topLeft">
                <circle cx="30" cy="60" r="2" fill="rgba(255,255,255,0.8)" />
                <circle cx="60" cy="40" r="1.5" fill="rgba(255,255,255,0.6)" />
                <circle cx="80" cy="80" r="2" fill="rgba(255,255,255,0.7)" />
                <circle cx="50" cy="100" r="1.5" fill="rgba(255,255,255,0.5)" />
                <line x1="30" y1="60" x2="60" y2="40" stroke="url(#lineGradient)" strokeWidth="0.5" />
                <line x1="60" y1="40" x2="80" y2="80" stroke="url(#lineGradient)" strokeWidth="0.5" />
                <line x1="80" y1="80" x2="50" y2="100" stroke="url(#lineGradient)" strokeWidth="0.5" />
                <line x1="50" y1="100" x2="30" y2="60" stroke="url(#lineGradient)" strokeWidth="0.5" />
              </g>
              {/* 左下星座 */}
              <g className={styles.constellation} data-pos="bottomLeft">
                <circle cx="40" cy="320" r="2" fill="rgba(255,255,255,0.7)" />
                <circle cx="70" cy="340" r="1.5" fill="rgba(255,255,255,0.5)" />
                <circle cx="90" cy="310" r="2" fill="rgba(255,255,255,0.6)" />
                <line x1="40" y1="320" x2="70" y2="340" stroke="url(#lineGradient)" strokeWidth="0.5" />
                <line x1="70" y1="340" x2="90" y2="310" stroke="url(#lineGradient)" strokeWidth="0.5" />
                <line x1="90" y1="310" x2="40" y2="320" stroke="url(#lineGradient)" strokeWidth="0.5" />
              </g>
            </svg>
            
            {/* 浮动粒子 */}
            <div className={styles.floatingParticles}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div 
                  key={i} 
                  className={styles.particle}
                  style={{
                    '--delay': `${i * 0.5}s`,
                    '--x': `${20 + Math.random() * 60}%`,
                    '--y': `${10 + Math.random() * 80}%`,
                  }}
                />
              ))}
            </div>
            
            {/* 装饰文字 */}
            <div className={styles.decorText} data-pos="top">
              <span>ARCHETYPE</span>
              <span className={styles.decorDot}>◇</span>
              <span>ANALYSIS</span>
            </div>
            <div className={styles.decorText} data-pos="bottom">
              <span>{primary.planet}</span>
            </div>
            
            {/* 能量指示器 */}
            <div className={styles.energyIndicator}>
              <div className={styles.energyLabel}>ENERGY</div>
              <div className={styles.energyMeter}>
                <div 
                  className={styles.energyLevel} 
                  style={{ height: `${Math.min((state.scores[primaryKey] || 0) / 20 * 100, 100)}%` }}
                />
              </div>
              <div className={styles.energyValue}>{state.scores[primaryKey] || 0}</div>
            </div>
            
            {/* 坐标装饰 */}
            <div className={styles.coordinates}>
              <span>X: {(Math.random() * 100).toFixed(2)}</span>
              <span>Y: {(Math.random() * 100).toFixed(2)}</span>
              <span>Z: {(Math.random() * 100).toFixed(2)}</span>
            </div>
            
            {/* 角落装饰框 */}
            <div className={styles.cornerFrame} data-corner="tl" />
            <div className={styles.cornerFrame} data-corner="bl" />
          </div>
          
          <PlanetCanvas primary={primary} secondary={secondary} all={all} />
        </div>

        <div
          className={styles.right}
          style={{ transform: `translateY(${-parallaxOffset * 0.2}px)` }}
        >
          <div className={styles.titleRow}>
            <h1 className={styles.title}>
              {primary.name} · {primary.planet}
            </h1>
            {/* 稀有度徽章 */}
            <div className={styles.rarityBadge}>
              <img src="/images/huizhang.png" alt="徽章" className={styles.badgeImg} />
              <div className={styles.badgeInfo}>
                <span className={styles.badgePercent}>{rarityPercent}%</span>
                <span className={styles.badgeLabel}>稀有度</span>
              </div>
            </div>
          </div>

          {/* 次要人格类型 */}
          {secondary && (
            <div className={styles.secondaryType}>
              <span className={styles.secondaryLabel}>次要原型</span>
              <span className={styles.secondaryName}>{secondary.name} · {secondary.planet}</span>
            </div>
          )}

          <div className={styles.sections}>
            <div className={`${styles.section} ${textVisible.feature ? styles.visible : ''}`}>
              <h3>你的星图解读</h3>
              <p>{personalizedTexts.feature}</p>
            </div>

            <div className={`${styles.section} ${textVisible.alienation ? styles.visible : ''}`}>
              <h3>暗面警示</h3>
              <p>{personalizedTexts.alienation}</p>
            </div>

            <div className={`${styles.section} ${textVisible.vacation ? styles.visible : ''}`}>
              <h3>平衡建议</h3>
              <p>{personalizedTexts.vacation}</p>
            </div>
          </div>

          <div className={styles.buttons}>
            <button className={styles.radarBtn} onClick={handleRadarClick}>
              ◇ 查看雷达图
            </button>
            <button className={styles.oracleBtn} onClick={handleOracleClick}>
              ✦ 星际祭司
            </button>
            <button className={styles.landscapeBtn} onClick={() => setShowLandscape(true)}>
              ✧ 星球风景
            </button>
            <button className={styles.drawBtn} onClick={handleDrawingClick}>
              绘制自画像
            </button>
            <button className={styles.voucherBtn} onClick={() => setShowVoucher(true)}>
              🎫 打印体验券
            </button>
            <button className={styles.shareBtn} onClick={handleShare}>
              生成星蚀卡片
            </button>
          </div>
        </div>
      </div>

      {/* 雷达图弹窗 */}
      {showRadar && (
        <div className={styles.radarOverlay} onClick={() => setShowRadar(false)}>
          {/* 背景星星 */}
          <div className={styles.radarStars}>
            {Array.from({ length: 50 }).map((_, i) => (
              <div 
                key={i} 
                className={styles.radarStar}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                }}
              />
            ))}
          </div>
          
          <div className={styles.radarModal} onClick={e => e.stopPropagation()}>
            {/* 装饰角落 */}
            <div className={styles.radarCorner} data-pos="tl" />
            <div className={styles.radarCorner} data-pos="tr" />
            <div className={styles.radarCorner} data-pos="bl" />
            <div className={styles.radarCorner} data-pos="br" />
            
            <button className={styles.closeBtn} onClick={() => setShowRadar(false)}>×</button>
            
            {/* 标题区 */}
            <div className={styles.radarHeader}>
              <div className={styles.radarTitleDecor}>◇ ◇ ◇</div>
              <h2 className={styles.radarTitle}>原型能量图谱</h2>
              <p className={styles.radarSubtitle}>探索你内心的十二种原始力量</p>
            </div>
            
            <div className={styles.radarContent}>
              {/* 左侧雷达图 */}
              <div className={styles.radarChartSection}>
                <div className={styles.radarChartWrapper}>
                  <div className={styles.radarChart}>
                    <RadarChart data={all} />
                  </div>
                  {/* 装饰光环 */}
                  <div className={styles.radarGlow} />
                </div>
                <div className={styles.radarLegend}>
                  <div className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: primary.color }} />
                    <span>主导: {primary.name}</span>
                  </div>
                  {secondary && (
                    <div className={styles.legendItem}>
                      <span className={styles.legendDot} style={{ background: secondary.color }} />
                      <span>次要: {secondary.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 右侧详情 */}
              <div className={styles.radarDetails}>
                {/* 主要原型卡片 */}
                <div className={styles.primaryCard}>
                  <div className={styles.primaryCardGlow} style={{ background: primary.color }} />
                  <img src={primary.planetImg} alt="" className={styles.primaryPlanetImg} />
                  <div className={styles.primaryInfo}>
                    <div className={styles.primaryLabel}>✦ 主导原型</div>
                    <div className={styles.primaryName}>{primary.name}</div>
                    <div className={styles.primaryPlanet}>{primary.planet}</div>
                  </div>
                  <div className={styles.primaryScoreBox}>
                    <div className={styles.primaryScore}>{state.scores[primaryKey] || 0}</div>
                    <div className={styles.primaryScoreLabel}>能量值</div>
                  </div>
                </div>

                {/* 次要原型 */}
                {secondary && (
                  <div className={styles.secondaryCard}>
                    <img src={secondary.planetImg} alt="" className={styles.secondaryPlanetImg} />
                    <div className={styles.secondaryInfo}>
                      <div className={styles.secondaryLabel}>次要原型</div>
                      <div className={styles.secondaryCardName}>{secondary.name} · {secondary.planet}</div>
                    </div>
                  </div>
                )}

                {/* 全部能量分布 */}
                <div className={styles.energySection}>
                  <h4 className={styles.energySectionTitle}>◇ 能量分布</h4>
                  <div className={styles.energyBars}>
                    {all.sort((a, b) => b.score - a.score).slice(0, 6).map((arch) => (
                      <div key={arch.key} className={styles.energyBar}>
                        <img src={arch.planetImg} alt="" className={styles.energyIcon} />
                        <span className={styles.energyName}>{arch.name}</span>
                        <div className={styles.energyTrack}>
                          <div 
                            className={styles.energyFill} 
                            style={{ 
                              width: `${(arch.score / Math.max(...all.map(a => a.score))) * 100}%`,
                              background: `linear-gradient(90deg, ${arch.color}88, ${arch.color})`,
                            }} 
                          />
                        </div>
                        <span className={styles.energyValue}>{arch.score}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 相性星球推荐 */}
                <div className={styles.compatSection}>
                  <h4 className={styles.compatTitle}>◇ 相性星球</h4>
                  <p className={styles.compatDesc}>与你能量共振的原型</p>
                  <div className={styles.compatList}>
                    {compatiblePlanets.complement.map(planet => (
                      <div 
                        key={planet.key} 
                        className={styles.compatItem}
                      >
                        <div className={styles.compatItemGlow} style={{ background: planet.color }} />
                        <img src={planet.planetImg} alt="" className={styles.compatImg} />
                        <div className={styles.compatInfo}>
                          <span className={styles.compatName}>{planet.name}</span>
                          <span className={styles.compatPlanet}>{planet.planet}</span>
                          <span className={styles.compatType}>{planet.type}</span>
                        </div>
                      </div>
                    ))}
                    {compatiblePlanets.mirror.map(planet => (
                      <div 
                        key={planet.key} 
                        className={styles.compatItem}
                      >
                        <div className={styles.compatItemGlow} style={{ background: planet.color }} />
                        <img src={planet.planetImg} alt="" className={styles.compatImg} />
                        <div className={styles.compatInfo}>
                          <span className={styles.compatName}>{planet.name}</span>
                          <span className={styles.compatPlanet}>{planet.planet}</span>
                          <span className={styles.compatType}>{planet.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* 底部装饰 */}
            <div className={styles.radarFooter}>
              <div className={styles.radarFooterLine} />
              <span className={styles.radarFooterText}>ARCHETYPE ENERGY MAP</span>
              <div className={styles.radarFooterLine} />
            </div>
          </div>
        </div>
      )}

      {showDrawing && (
        <DrawingCanvas
          archetype={`${primary.name} · ${primary.planet}`}
          onSave={() => setShowDrawing(false)}
          onClose={() => setShowDrawing(false)}
        />
      )}

      {showOracle && (
        <Oracle
          archetype={primaryKey}
          archetypeInfo={primary}
          onClose={() => setShowOracle(false)}
        />
      )}

      {showLandscape && (
        <PlanetLandscape
          scores={state.scores}
          onSave={() => setShowLandscape(false)}
          onClose={() => setShowLandscape(false)}
          onImageGenerated={(url) => setLandscapeImageUrl(url)}
        />
      )}

      {showVoucher && (
        <VoucherPrint
          archetypeKey={primaryKey}
          landscapeImageUrl={landscapeImageUrl}
          onClose={() => setShowVoucher(false)}
          onNeedLandscape={() => {
            setShowVoucher(false);
            setShowLandscape(true);
          }}
        />
      )}
    </div>
  );
}
